/**
 * Cowork API client — talks to the agent backend (cowork-cloud).
 *
 * Auth: the user's existing Beatly JWT. The cowork backend verifies platform
 * tokens against api-v2, so no separate login or token exchange is needed.
 *
 * Entirely additive: nothing here touches existing omni-app modules, and every
 * call degrades to an empty/false result when VITE_COWORK_API_URL is unset,
 * so the app behaves exactly as before when the integration is off.
 */
import { getTokenFromStorage } from '@/utils/auth';
import { staffScopeHeaders } from '@/cowork/staffScope';

export const COWORK_API_URL = (import.meta.env.VITE_COWORK_API_URL as string | undefined) || '';

/** True when the cowork backend is configured for this build. */
export const isCoworkLive = (): boolean => COWORK_API_URL.length > 0;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!isCoworkLive()) throw new Error('Cowork backend not configured');
  const token = getTokenFromStorage();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${COWORK_API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...staffScopeHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { message?: string; error?: string }).message
      || (detail as { error?: string }).error
      || `Cowork API ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Types (mirror the cowork API surface we consume) ──────────────

export interface CoworkAgent {
  id: string;
  name: string;
  title: string;
  tagline?: string;
  avatarEmoji?: string;
  avatarUrl?: string;
  accentColor?: string;
  isActive: boolean;
}

export interface CoworkTask {
  id: string;
  title: string;
  description?: string;
  status: 'queue' | 'working' | 'review' | 'done' | 'failed';
  assignedAgent?: { id: string; name: string; avatarEmoji?: string; avatarUrl?: string } | null;
  updatedAt?: string;
}

export interface CampaignRoomState {
  campaign: Record<string, unknown> & { name?: string; status?: string };
  curation: { curation?: Array<Record<string, unknown>> } | null;
  drafts: { drafts?: Array<Record<string, unknown>> } | null;
  agentFeed: Array<{ taskId: string; title: string; agentId: string; status: string; lastEvent: string; at: string }>;
}

// ── Calls ─────────────────────────────────────────────────────────

export const listAgents = () => request<CoworkAgent[]>('/v1/agents');

export const listTasks = () =>
  request<{ teamId: string; tasks: CoworkTask[] }>('/v1/tasks');

export const getCampaignRoom = (campaignId: string) =>
  request<CampaignRoomState>(`/v1/campaign-room/${campaignId}`);

export const acceptCuration = (curationId: string) =>
  request(`/v1/campaign-room/curation/${curationId}/accept`, { method: 'POST', body: '{}' });

export const rejectCuration = (curationId: string, reason?: string) =>
  request(`/v1/campaign-room/curation/${curationId}/reject`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });

export interface CoworkCampaignListItem {
  campaign_id: string;
  name: string;
  status?: string;
  brand_name?: string;
  date_start?: string;
  date_end?: string;
}

export const listCampaigns = () =>
  request<{ campaigns: CoworkCampaignListItem[] }>('/v1/campaign-room');

/** Absolute URL for an asset served by the cowork backend (e.g. agent avatars). */
/** One draft with the client and its approve-by day (cowork GET /v1/campaign-room/:id/client-review). */
export interface ClientReviewDate { productId: string; versionId: string; reachedOn: string; approveBy: string; slaDays: number | null }
export interface ClientReviewDates { campaignId: string; checkedAt: string | null; drafts: ClientReviewDate[] }

/**
 * The approve-by day of each draft waiting on the client (staging sim "Slow client", 24 Oct 2026): the agents'
 * conductor keeps it; cowork answers only after the caller's own Beatly JWT reads the campaign in the client API.
 */
export const getClientReviewDates = (campaignId: string) =>
  request<ClientReviewDates>(`/v1/campaign-room/${encodeURIComponent(campaignId)}/client-review`);

export const coworkAssetUrl = (p?: string): string | undefined =>
  p ? (p.startsWith('http') ? p : `${COWORK_API_URL}${p}`) : undefined;

export const createTask = (body: { title: string; description?: string; assignedAgentId: string }) =>
  request<{ id: string }>('/v1/tasks', { method: 'POST', body: JSON.stringify(body) });
